/**
 * Runs `fn` once the page has loaded and the browser has reported its first contentful paint, so work the
 * first screen doesn't need (the hero's three.js chunk, the film's poster) never competes with it. A frame
 * callback alone isn't enough: the paint is presented a few milliseconds after the frame's tasks run.
 */
export function afterFirstPaint(fn: () => void): void {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    setTimeout(fn, 0);
  };
  const painted = () => performance.getEntriesByName('first-contentful-paint').length > 0;
  const watch = () => {
    if (painted()) return run();
    if (!PerformanceObserver.supportedEntryTypes?.includes('paint')) {
      requestAnimationFrame(() => setTimeout(run, 50));
      return;
    }
    const po = new PerformanceObserver(() => {
      if (!painted()) return;
      po.disconnect();
      run();
    });
    po.observe({ type: 'paint', buffered: true });
    // A tab opened in the background paints nothing until it is shown; don't wait on it forever.
    setTimeout(() => {
      po.disconnect();
      run();
    }, 4000);
  };
  if (document.readyState === 'complete') watch();
  else addEventListener('load', watch, { once: true });
}
